import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import WhitelistTable from './WhitelistTable';

import adminAuthService from '../../services/secureAdminAuthService';
import { useToast } from '../Toast/ToastContext';


interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  lastLogin: string;
}

interface DashboardStats {
  total_registrations: number;
  today_registrations: number;
  emails_sent: number;
  emails_pending: number;
  recent_registrations: Array<{
    id: string;
    name: string;
    email: string;
    wallet_address: string;
    created_at: string;
    email_sent: boolean;
  }>;
}

interface WhitelistEntry {
  id: string;
  name: string;
  email: string;
  wallet_address: string;
  created_at: string;
  email_sent: boolean;
  ip_address?: string;
  user_agent?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type ViewMode = 'login' | 'dashboard' | 'table';

interface AdminPanelState {
  viewMode: ViewMode;
}



const AdminPanel: React.FC = () => {
  const [panelState, setPanelState] = useState<AdminPanelState>({
    viewMode: 'login',
  });

  const [user, setUser] = useState<AdminUser | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { showSuccess, showError } = useToast();


  // Load functions (memoized to prevent unnecessary re-renders)
  const loadDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminAuthService.getDashboardStats();
      if (result.success && result.data) {
        setStats(result.data as DashboardStats);
      } else {
        // Provide fallback data when database is not available
        console.warn('Using fallback dashboard data due to database error:', result.error);
        setStats({
          total_registrations: 0,
          today_registrations: 0,
          emails_sent: 0,
          emails_pending: 0,
          recent_registrations: []
        });
        showError('Database Warning', 'Using offline data. Please check database connection.');
      }
    } catch (error) {
      console.error('Load stats error:', error);
      // Provide fallback data
      setStats({
        total_registrations: 0,
        today_registrations: 0,
        emails_sent: 0,
        emails_pending: 0,
        recent_registrations: []
      });
      showError('Connection Error', 'Database unavailable. Using offline mode.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadWhitelistEntries = useCallback(async (page: number = 1, search: string = '') => {
    setLoading(true);
    try {
      const result = await adminAuthService.getWhitelistEntries(page, pagination.limit, search);
      if (result.success && result.data && result.pagination) {
        setEntries(result.data as WhitelistEntry[]);
        setPagination(result.pagination as Pagination);
      } else {
        // Provide fallback data when database is not available
        console.warn('Using fallback entries data due to database error:', result.error);
        setEntries([]);
        setPagination({
          page: 1,
          limit: pagination.limit,
          total: 0,
          totalPages: 0
        });
        showError('Database Warning', 'No entries available. Please check database connection.');
      }
    } catch (error) {
      console.error('Load entries error:', error);
      // Provide fallback data
      setEntries([]);
      setPagination({
        page: 1,
        limit: pagination.limit,
        total: 0,
        totalPages: 0
      });
      showError('Connection Error', 'Database unavailable. Using offline mode.');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, showError]);



  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      const currentUser = adminAuthService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setPanelState({ viewMode: 'dashboard' });
        loadDashboardStats();
      }
    }
  }, [loadDashboardStats]);

  const handleLogin = useCallback(async (credentials: { username: string; password: string }) => {
    setLoading(true);
    setLoginError('');

    try {
      // Get IP address from the browser (this is a basic approach)
      // In a real production environment, you might want to get this from a server endpoint
      const ipAddress = localStorage.getItem('user-ip') || null; // We'll set this via a function call
      
      const result = await adminAuthService.login(credentials.username, credentials.password, ipAddress);
      
      if (result.success && result.user) {
        setUser(result.user);
        setPanelState({ viewMode: 'dashboard' });
        showSuccess('Login Successful', `Welcome back, ${result.user.username}!`);
        loadDashboardStats();
      } else {
        setLoginError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [showSuccess, loadDashboardStats]);

  const handleLogout = useCallback(() => {
    adminAuthService.logout();
    setUser(null);
    setStats(null);
    setEntries([]);
    setPanelState({ viewMode: 'login' });
    showSuccess('Logged Out', 'You have been successfully logged out');
  }, [showSuccess]);

  const handleViewAllEntries = useCallback(() => {
    setPanelState({ viewMode: 'table' });
    loadWhitelistEntries(1, searchTerm);
  }, [loadWhitelistEntries, searchTerm]);

  const handleBackToDashboard = useCallback(() => {
    setPanelState({ viewMode: 'dashboard' });
    loadDashboardStats();
  }, [loadDashboardStats]);

  const handlePageChange = useCallback((page: number) => {
    loadWhitelistEntries(page, searchTerm);
  }, [searchTerm, loadWhitelistEntries]);

  const handleSearch = useCallback((search: string) => {
    setSearchTerm(search);
    loadWhitelistEntries(1, search);
  }, [loadWhitelistEntries]);

  const handleExport = useCallback(async () => {
    try {
      const result = await adminAuthService.exportToCSV();
      if (result.success) {
        showSuccess('Export Successful', 'Whitelist data has been exported to CSV');
      } else {
        showError('Export Failed', result.error || 'Failed to export data');
      }
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', 'An unexpected error occurred during export');
    }
  }, [showSuccess, showError]);

  const handleDeleteEntry = useCallback(async (entryId: string) => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await adminAuthService.deleteEntry(entryId);
      if (result.success) {
        showSuccess('Entry Deleted', 'The whitelist entry has been deleted');
        // Reload current page
        loadWhitelistEntries(pagination.page, searchTerm);
      } else {
        showError('Delete Failed', result.error || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showError('Delete Failed', 'An unexpected error occurred');
    }
  }, [pagination.page, searchTerm, showSuccess, showError, loadWhitelistEntries]);

  const handleUpdateEmailStatus = useCallback(async (entryId: string, emailSent: boolean) => {
    try {
      const result = await adminAuthService.updateEmailStatus(entryId, emailSent);
      if (result.success) {
        showSuccess(
          'Status Updated', 
          `Email status updated to ${emailSent ? 'sent' : 'pending'}`
        );
        // Reload current page
        loadWhitelistEntries(pagination.page, searchTerm);
      } else {
        showError('Update Failed', result.error || 'Failed to update email status');
      }
    } catch (error) {
      console.error('Update error:', error);
      showError('Update Failed', 'An unexpected error occurred');
    }
  }, [pagination.page, searchTerm, showSuccess, showError, loadWhitelistEntries]);

  const handleRefresh = useCallback(() => {
    if (panelState.viewMode === 'dashboard') {
      loadDashboardStats();
    } else if (panelState.viewMode === 'table') {
      loadWhitelistEntries(pagination.page, searchTerm);
    }
  }, [panelState.viewMode, pagination.page, searchTerm, loadDashboardStats, loadWhitelistEntries]);



  return (
    <div className="relative w-full h-full overflow-hidden">
      {panelState.viewMode === 'login' && (
        <AdminLogin
          onLogin={handleLogin}
          loading={loading}
          error={loginError}
        />
      )}

      {panelState.viewMode === 'dashboard' && user && (
        <AdminDashboard
          user={user}
          onLogout={handleLogout}
          stats={stats}
          loading={loading}
          onRefresh={handleRefresh}
          onViewAllEntries={handleViewAllEntries}
        />
      )}

      {panelState.viewMode === 'table' && (
        <WhitelistTable
          entries={entries}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          onExport={handleExport}
          onDelete={handleDeleteEntry}
          onUpdateEmailStatus={handleUpdateEmailStatus}
          onBack={handleBackToDashboard}
        />
      )}
    </div>
  );
};

export default AdminPanel;